import useConsentCookie from '../hooks/useConsentCookie';
import { isAMD } from '../utils';
import { amplitudeExperiment } from './AmplitudeExperimentSDK';
import { useEffect, useState } from 'react';

/**
 * @param {string} experimentKey - The experiment key
 * @param {Object.<string, any>} [audienceAttributes]  (optional) - attributes that identify the current user.
 * @returns {string | null} The experiment variation string or null.
 * @example <caption>Get experiment variant without overrides </caption>
 * const experimentVariant = useExperimentVariationAnon({ experimentKey: 'grow-123_droplets_experiment' });
 * @example <caption>Get experiment variant with audienceAttributes</caption>
 * const experimentVariant = useExperimentVariationAnon({ experimentKey: 'grow-123_droplets_experiment', audienceAttributes: { has_droplets: true } });
 **/

interface ExperimentVariationProps {
  experimentKey: string;
  audienceAttributes?: { [name: string]: any };
}

export const useExperimentVariationAnon = ({
  experimentKey,
  audienceAttributes
}: ExperimentVariationProps): [string | null, boolean] => {
  const hasConsent = useConsentCookie();
  const [variant, setVariant] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (!hasConsent || isAMD()) {
      setVariant(null);
      setLoading(false);
      return;
    }

    const fetchAmplitudeFlagWithAudience = async (): Promise<void> => {
      try {
        await amplitudeExperiment.fetch({
          user_properties: audienceAttributes
        });

        // get variant after fetching
        const updatedVariant = amplitudeExperiment.variant(experimentKey);
        setVariant(updatedVariant?.value ?? null);
      } finally {
        setLoading(false);
      }
    };

    fetchAmplitudeFlagWithAudience();
  }, [hasConsent, experimentKey, audienceAttributes]);

  return [variant, loading];
};
